import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  Flame,
  GraduationCap,
  Grid3x3,
  HeartPulse,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Move,
  Package,
  Plus,
  ScanLine,
  Scissors,
  Sparkles,
  SquareStack,
  Stethoscope,
  Sun,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/skin-grafting-surgery-mumbai";
const BURNS_PATH = "/burns-burn-scar-treatment-mumbai";
const WOUND_CARE_PATH = "/diabetic-foot-surgery-in-mumbai";
const SCAR_REVISION_PATH = "/hypertrophic-scar-treatment-in-mumbai";

const SKIN_GRAFT_FAQS = [
  {
    question: "What is graft take and how do I know if my graft has taken successfully?",
    answer:
      "Graft take is the process by which the transplanted skin establishes a blood supply from the wound bed and integrates permanently. A successfully taken graft appears pink or red and firmly adhered to the wound bed when the bolster dressing is removed at day 5 to 7. A graft that has not taken appears pale, grey, or dark, and may separate from the wound bed. Partial graft take, where some areas have integrated and others have not, is managed with ongoing wound care and sometimes with additional small grafts to the non-taken areas.",
  },
  {
    question: "How long does skin grafting recovery take?",
    answer:
      "The wound is closed within 2 to 3 weeks in most successfully grafted cases. Full recovery including donor site healing, graft maturation, and return to normal activity takes 1 to 3 months depending on the extent of the graft and the patient's overall health. Cosmetic improvement continues for 6 to 18 months as the graft tissue matures.",
  },
  {
    question: "Will the skin graft match my normal skin color?",
    answer:
      "Color match depends on the graft type and donor site selection. Full-thickness grafts from donor areas close to the recipient site in skin color produce the best match. Split-thickness grafts from distant donor areas such as the thigh often produce a color difference. Color mismatch improves over months as the graft matures and can be further improved with laser therapy after full maturation.",
  },
  {
    question: "Does the donor site leave a scar?",
    answer:
      "Split-thickness donor sites heal within 10 to 14 days leaving a flat, slightly pigmented area that is less conspicuous than the original wound but represents a new surface change. Full-thickness donor sites are sutured closed and leave a linear scar. Donor site healing is usually less significant to patients than the primary wound being treated, but scar care with silicone therapy applies to the donor site as well.",
  },
  {
    question: "What happens if the skin graft fails to take?",
    answer:
      "Partial or complete graft failure is managed by continued wound care to allow the wound bed to prepare for a second grafting attempt. In areas of partial take, the areas that did not integrate are managed with wound care while the successfully taken areas are maintained. A second grafting procedure can be performed once the wound bed has recovered. Graft failure does not mean grafting cannot succeed. It means the conditions for take were not met and the approach is adjusted before attempting again.",
  },
  {
    question: "Can I move the grafted area during healing?",
    answer:
      "Movement of the graft relative to the wound bed in the first 5 to 7 days destroys the new blood vessel connections forming during graft take and is a primary cause of graft failure. The grafted area is immobilized during this critical period with splints and restricted activity. After the bolster is removed and take is confirmed, graduated mobilization begins under the guidance of the surgical and physiotherapy team.",
  },
  {
    question: "Is skin grafting painful?",
    answer:
      "Surgery is performed under general or regional anesthesia. The donor site produces more post-operative discomfort than the grafted wound in most patients, as the partial-thickness skin removal from the donor site creates a superficial wound that is sensitive during healing. This is managed with appropriate dressing and pain medication. Most patients describe the donor site as the more uncomfortable aspect of the recovery rather than the grafted wound itself.",
  },
  {
    question: "When can skin graft cosmetic results be further improved?",
    answer:
      "Laser therapy and fat grafting for cosmetic improvement of skin grafts are most appropriate after the graft has fully matured, typically 6 to 12 months after surgery. Operating on a graft before full maturation risks disrupting the still-remodeling tissue. Once mature, ablative fractional laser improves surface texture and pigmentation, and fat grafting improves volume, pliability, and skin quality in atrophic grafted areas. These adjunctive treatments significantly improve the final cosmetic result beyond what the graft alone achieves.",
  },
];

const skinBodyLocation = { "@type": "AnatomicalStructure", name: "Skin" };
const plasticSpecialty = "https://schema.org/PlasticSurgery";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Skin grafting surgery",
      description:
        "Reconstructive transplantation of autologous skin from a donor site to a recipient wound for permanent coverage when primary healing is not possible.",
      procedureType: "SurgicalProcedure",
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Split-thickness skin graft",
      description:
        "Harvest of epidermis and partial dermis with dermatome for large-area coverage; donor site re-epithelializes from residual dermis.",
      procedureType: "SurgicalProcedure",
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Full-thickness skin graft",
      description:
        "Excision of full-thickness skin from a donor site with primary closure; used for cosmetically critical areas with favorable color match.",
      procedureType: "SurgicalProcedure",
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Meshed skin graft",
      description:
        "Split-thickness graft expanded through meshing to cover larger recipient areas; characteristic mesh pattern may persist after healing.",
      procedureType: "SurgicalProcedure",
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: SKIN_GRAFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function SkinGraftJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Skin Grafting Surgery", href: "/skin-grafting-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-12 pb-16 md:pb-24" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Reconstructive Surgery</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-premium-purple leading-[1.15] mb-6">Skin Grafting Surgery</h1>
            <p className="text-lg text-deep-navy/70 mb-8">
              Reconstructive Skin Graft Procedures by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">Board Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Skin Grafting" icon="🩹" size="hero"  imageSrc={HERO.skinGrafting} imageAlt="Skin grafting"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Skin Grafting?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Skin grafting is a reconstructive surgical procedure that transplants healthy skin from one area of the body, the donor site, to another area where skin has been lost or damaged, the recipient site. The grafted skin establishes a new blood supply from the wound bed beneath it, integrates with the surrounding tissue, and provides permanent wound coverage. Skin grafting is not a cosmetic procedure in its primary application. It is a reconstructive necessity for wounds that cannot heal on their own.
            </p>
            <p>
              The indication for skin grafting is a wound that cannot close by primary suturing and cannot heal adequately through natural secondary intention within a clinically acceptable timeframe. Deep burns that have destroyed the full thickness of the skin, traumatic wounds with significant skin loss, diabetic and other chronic ulcers that have failed to heal with wound care, and surgical defects after tumor removal are the most common indications. In all of these situations, the wound bed exists and is capable of supporting graft take, but lacks the skin surface layer needed to complete healing.
            </p>
            <p>
              Dr. Vicky Jain performs skin grafting surgery for patients with burns, chronic wounds, traumatic skin loss, and post-surgical defects. Graft type selection, donor site choice, and wound bed preparation are planned individually based on the wound size, depth, location, and what cosmetic outcome is expected at the recipient site.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Dr. Jain&apos;s Approach to Skin Grafting</h2>
            <div className="space-y-6 text-deep-navy/70 leading-relaxed">
              <p>
                The most important factor determining graft success is the quality of the wound bed at the recipient site. A wound bed with healthy granulation tissue, adequate blood supply, and no active infection provides the conditions the graft needs to establish its own blood supply, a process called graft take. A wound bed that is infected, ischemic, heavily contaminated, or covered with necrotic tissue does not support graft take and will result in graft failure regardless of technique. Wound bed preparation before grafting is therefore as important as the grafting procedure itself.
              </p>
              <p>
                Donor site selection and graft type are chosen based on the recipient site requirements. Facial and visible area wounds need the best possible cosmetic match and minimal contraction, which favors full-thickness grafts from skin-matched donor areas such as behind the ear, the upper eyelid, or the supraclavicular region. Large wounds, burns, and chronic ulcers need maximum coverage area, which favors split-thickness grafts from the thigh or buttock. Post-operative graft immobilization during the first 5 to 7 days is critical to allow the graft to establish vascular connections without disruption.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #FAFAF8 0%, rgba(250, 250, 248, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-premium-purple">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhenNeededSection() {
  const cards = [
    {
      title: "Deep Burns",
      icon: Flame,
      copy: "Full-thickness burns that have destroyed the entire skin layer cannot heal without surgical skin replacement. Early skin grafting of full-thickness burns reduces infection risk, shortens hospital stay, and produces better functional and cosmetic outcomes than delayed grafting. The timing and staging of burn grafting depends on the burn extent and the patient's overall condition.",
    },
    {
      title: "Traumatic Skin Loss",
      icon: Stethoscope,
      copy: "Road accidents, crush injuries, degloving injuries, and wounds from machinery can result in loss of skin over large areas. When primary wound closure is not possible due to the extent of skin loss, skin grafting provides coverage. Wound bed preparation and infection management precede grafting in contaminated traumatic wounds.",
    },
    {
      title: "Chronic Non-Healing Wounds",
      icon: HeartPulse,
      copy: "Diabetic foot ulcers, venous leg ulcers, pressure sores, and other chronic wounds that have failed to heal with wound care alone often benefit from skin grafting once the wound bed has been adequately prepared and any underlying condition optimized. Grafting accelerates closure and reduces the risk of wound-related complications.",
    },
    {
      title: "Post-Surgical Defects",
      icon: Scissors,
      copy: "After removal of tumors, cysts, or extensive scar tissue, the resulting defect may be too large to close primarily. Skin grafting provides coverage of these surgical defects. The graft type chosen depends on the depth of the defect, the location, and the cosmetic requirements of the recipient site.",
    },
    {
      title: "Contracture Release",
      icon: Move,
      copy: "When burn contractures or other scar contractures are released surgically, the resulting defect after the scar band is divided must be covered. Skin grafts or flaps provide this coverage. The graft is applied to the released contracture site and post-operative splinting maintains the gained range of movement during healing.",
    },
    {
      title: "Skin Cancer Reconstruction",
      icon: ScanLine,
      copy: "Excision of skin cancers, particularly basal cell carcinoma and squamous cell carcinoma on the face and scalp, can leave defects that require grafting when local tissue rearrangement is not sufficient to close the wound. Full-thickness grafts from skin-matched donor sites produce the best cosmetic results for facial reconstruction.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="when-needed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">When is Skin Grafting Required?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const GRAFT_TYPES = [
  {
    title: "Split-Thickness Skin Graft (STSG)",
    icon: Layers,
    body: "The most commonly used graft type. A thin layer consisting of the epidermis and a partial thickness of the dermis is harvested using a dermatome from a donor area, typically the thigh or buttock. The relatively thin graft takes more readily than full-thickness grafts because it requires less blood supply to sustain it during the take period. Split-thickness grafts can cover large areas from a single donor harvest and the donor site re-epithelializes from the remaining dermis within 10 to 14 days. The trade-offs are a higher contraction rate than full-thickness grafts and a less cosmetically refined appearance. Best for large wounds, burns, and chronic ulcers where coverage area is the priority.",
  },
  {
    title: "Full-Thickness Skin Graft (FTSG)",
    icon: SquareStack,
    body: "The entire skin thickness including epidermis and full dermis is harvested. Full-thickness grafts contract less than split-thickness grafts, produce a better color match when taken from skin-tone-matched donor sites, and provide a more cosmetically refined result. The donor site must be closed primarily by suture, limiting the available graft area. Best for facial defects, hand reconstruction, and visible areas where cosmetic outcome is important and the defect size allows full-thickness coverage.",
  },
  {
    title: "Meshed Skin Graft",
    icon: Grid3x3,
    body: "A split-thickness graft is passed through a meshing device that creates a pattern of slits, allowing the graft to be expanded to cover a larger area than the harvested piece. The mesh pattern fills in with new epithelium during healing. Meshed grafts are used for large burn areas where donor sites are limited. The mesh pattern remains visible as a permanent texture in the healed graft, which is cosmetically less satisfactory than unmeshed grafts.",
  },
  {
    title: "Sheet Graft",
    icon: Sun,
    body: "An unmeshed split-thickness graft applied as a single continuous piece. Produces a smoother, less textured surface than meshed grafts. Used on the face and hands where cosmetic appearance is more important than coverage efficiency.",
  },
  {
    title: "Composite Graft",
    icon: Package,
    body: "Includes skin plus underlying tissue such as fat or cartilage. Used for specialized reconstruction including ear and nose defects where the structural component is needed alongside the skin cover.",
  },
];

function TypesOfGraftsSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="types-of-grafts-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Skin Grafts</h2>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 max-w-4xl mx-auto">
          {GRAFT_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GraftTakeSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="graft-take-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">What is Graft Take and What Affects It?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Graft take is the process by which a skin graft establishes blood supply from the wound bed beneath it and integrates permanently with the recipient site. In the first 24 to 48 hours after grafting, the graft survives by absorbing nutrients directly from the wound bed fluid, a process called serum imbibition. Between days 2 and 5, new blood vessels from the wound bed grow into the graft and establish circulation. From day 5 onward the graft has its own blood supply and graft take is established.
            </p>
            <p>
              Several factors affect graft take. Wound bed quality is the most important: healthy granulation tissue with adequate blood supply supports take while infected, ischemic, or contaminated wound beds do not. Immobilization of the graft during the take period is critical. Any movement between the graft and the wound bed disrupts the new blood vessel connections forming in the first 5 days. Haematoma or seroma between the graft and wound bed physically prevents contact and blocks take. Infection introduces bacteria that digest the fibrin layer through which early blood vessel growth occurs. All post-operative protocols around dressing, immobilization, and infection management are designed to optimize these conditions for successful take.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_FLAP_ROWS = [
  {
    label: "Blood Supply",
    graft: "Derives blood supply from the recipient wound bed after grafting.",
    flap: "Brings its own blood supply with the transferred tissue.",
  },
  {
    label: "What is Transferred",
    graft: "Skin only (STSG or FTSG) or skin with superficial tissue (composite).",
    flap: "Skin, fat, muscle, and blood vessels transferred together as a unit.",
  },
  {
    label: "Wound Bed Requirement",
    graft: "Requires a healthy, well-vascularized wound bed for successful take.",
    flap: "Can cover wound beds with poor vascularity, exposed bone, tendon, or hardware, where grafts would fail.",
  },
  {
    label: "Complexity",
    graft: "Relatively straightforward. Appropriate for most superficial wound coverage.",
    flap: "More complex. Requires microsurgical technique for free flaps. Appropriate for complex reconstructive defects.",
  },
  {
    label: "Cosmetic Outcome",
    graft: "Good to excellent depending on graft type. Full-thickness grafts produce better cosmetic results than split-thickness.",
    flap: "Excellent when the flap tissue closely matches the recipient site in color, texture, and thickness.",
  },
  {
    label: "Best For",
    graft: "Large superficial wounds, burns, chronic ulcers, and post-surgical defects with a healthy wound bed.",
    flap: "Deep defects with exposed vital structures, wounds with poor vascularity, and complex facial or extremity reconstruction.",
  },
];

function VsFlapSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="vs-flap-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Skin Grafting vs. Flap Surgery</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Skin Graft
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">Flap Surgery</th>
              </tr>
            </thead>
            <tbody>
              {VS_FLAP_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.graft}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.flap}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          The choice between skin grafting and flap surgery is determined by the wound bed characteristics and what the defect requires for durable coverage and function. Many reconstructive patients receive skin grafting at one stage and flap surgery at another as part of a planned staged reconstruction.
        </p>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Skin Grafting Surgery is Performed</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 1 to 3 hours under general or regional anesthesia depending on the extent of the graft and the patient's condition. The wound is first assessed and prepared: any non-viable tissue, infection, or debris is debrided to create a clean, viable wound bed. The donor site is marked and the graft is harvested using a dermatome for split-thickness grafts or surgical excision with primary closure for full-thickness grafts.
            </p>
            <p>
              The graft is sized and shaped to fit the recipient wound and placed directly onto the wound bed. It is secured with sutures, staples, or fibrin glue to prevent movement during the take period. A bolster dressing, made from gauze that is tied over the graft, applies uniform pressure that holds the graft in contact with the wound bed and prevents fluid accumulation beneath it. The bolster remains in place for 5 to 7 days.
            </p>
            <p>
              The donor site is dressed with an appropriate wound care dressing that promotes re-epithelialization. Split-thickness donor sites heal within 10 to 14 days. Full-thickness donor sites are sutured closed and heal as a linear scar. Hospital admission is required for most skin grafting procedures, with length of stay depending on the wound extent and overall patient condition.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 5",
      description:
        "Graft immobilization phase. The bolster dressing stays in place. The graft is establishing its blood supply from the wound bed beneath. This is the most critical period. Any disruption to the graft during this phase affects take.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Bolster removed at first dressing change. Graft take is assessed. A successfully taken graft appears pink and adherent to the wound bed. Any areas of partial take are noted and managed with ongoing wound care.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Initial healing is complete for most successful grafts. The graft surface is epithelialized and the wound is closed. Donor site has re-epithelialized. Patients are mobilizing and returning to appropriate activity.",
      icon: Clock,
    },
    {
      period: "Months 1 to 6",
      description:
        "The graft matures. Redness reduces. Texture and color improve progressively. Scar management with silicone therapy and sun protection begins. Final cosmetic assessment is performed at 6 months.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CosmeticOutcomesSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="cosmetic-outcomes-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Cosmetic Outcomes of Skin Grafting</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Skin grafts rarely match the surrounding skin perfectly in color, texture, or surface quality immediately after healing. Color mismatch is the most common cosmetic limitation, particularly with split-thickness grafts taken from distant donor sites where the skin tone differs from the recipient area. Full-thickness grafts from skin-matched donor sites adjacent to the recipient area produce better color and texture matches than split-thickness grafts from the thigh or buttock.
            </p>
            <p>
              Cosmetic improvement continues over 6 to 18 months as the graft matures and the immune-mediated remodeling process improves texture and reduces redness. Laser therapy, particularly ablative fractional laser, can further improve graft surface quality, texture irregularity, and pigmentation mismatch after the graft has fully matured. Fat grafting beneath the graft improves skin quality and volume in atrophic grafted areas. Patients should understand that grafted skin is not identical to the original skin but represents a clinically functional and cosmetically improved result relative to the wound that preceded it.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoNeedsSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="who-needs-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Needs Skin Grafting Surgery?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Skin grafting is a reconstructive necessity rather than an elective choice for most patients who need it. The indication is a wound that cannot heal without surgical skin replacement. Any patient with a deep burn, significant traumatic skin loss, chronic wound that has failed conservative management, or post-surgical defect too large for primary closure is a candidate for assessment by a plastic surgeon to determine whether skin grafting is the appropriate reconstruction.
            </p>
            <p>
              Optimal candidates for skin grafting have wound beds that are clean, well-vascularized, and free of active infection before grafting. Where these conditions are not present, the preparatory phase of wound management, debridement, infection treatment, and granulation tissue development, must achieve them before grafting can be performed safely. Patients who smoke are asked to stop before and after surgery as nicotine impairs the microcirculation that the graft depends on for take. Patients with diabetes and other conditions affecting wound healing are managed with additional precautions specific to their condition.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Burn" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {SKIN_GRAFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`graft-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Skin Grafting Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the wound or defect, determines the appropriate graft type and donor site, and plans the wound bed preparation and grafting sequence needed to achieve successful coverage. For patients with established graft results who want cosmetic improvement, a scar management and adjunctive treatment plan is developed. You leave with a clear understanding of what the procedure involves and what the expected outcome is.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Burns & Burn Scar Treatment",
      description: "Comprehensive burn wound care and burn scar management alongside skin grafting.",
      href: BURNS_PATH,
    },
    {
      name: "Wound Care",
      description: "Wound bed preparation and ongoing management for patients requiring grafting or post-graft care.",
      href: WOUND_CARE_PATH,
    },
    {
      name: "Scar Revision",
      description: "Cosmetic improvement of skin graft results after full maturation through laser, fat grafting, and surgical revision.",
      href: SCAR_REVISION_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SkinGraftingPage() {
  return (
    <>
      <SEO
        title="Skin Grafting Surgery | Dr. Vicky Jain"
        description="Burns, chronic wounds, trauma, or surgical defects requiring skin replacement. Dr. Vicky Jain performs skin grafting surgery with high graft success. Book today."
        canonical="/skin-grafting-surgery-mumbai"
      />
      <SkinGraftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhenNeededSection />
      <TypesOfGraftsSection />
      <GraftTakeSection />
      <VsFlapSection />
      <HowPerformedSection />
      <RecoveryTimelineSection />
      <CosmeticOutcomesSection />
      <WhoNeedsSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
